/**
 * Resolve the rendered state of the per-row opener ON/OFF switch from the
 * AUTHORITATIVE backend state (spec D6/O7).
 *
 * The incident this guards against: the operator flips a rule OFF, the switch
 * optimistically paints OFF, but the write never durably disarmed the rule and
 * the engine kept trading — the UI lied. The fix: the switch's `checked` is
 * driven ONLY by the real state the backend echoes over the WS envelope
 * (`opening_state` / `opening_state_update`), NEVER by an optimistic cache
 * patch. So the switch can NEVER render OFF while the backend is still armed.
 *
 * The authoritative bit is the rule's REAL armed state (`ruleArmed`), derived
 * by the engine from the rule's durable `desiredArmed`/status — NOT the
 * pair-level `pairEnabled` kill switch. After an arm/disarm the switch settles
 * on the rule's actual armed state, so it can never show ON while the rule is
 * paused (or vice-versa). For BACK-COMPAT during rollout, when the envelope
 * omits `ruleArmed` (older backend), `resolveWsArmed` falls back to the
 * previous `pairEnabled` behaviour so nothing breaks before the backend ships.
 *
 * The operator's last on/off intent (`pending`) does NOT flip `checked`; it
 * only:
 *   - locks the control (`disabled`) so it can't be double-fired, and
 *   - surfaces an in-progress label ("Apagando…" / "Prendiendo…") so the
 *     operator sees the write is in flight and NOT yet applied.
 *
 * When the WS state echoes the intent (`wsArmed === pending`) the write is
 * `settled` — the caller clears `pending`, `checked` already shows the new
 * real value, and the label disappears. If the write fails (mutation onError
 * toast) or the echo never lands within a timeout, the caller clears `pending`
 * and the switch snaps back to the real `wsArmed` state.
 */
export interface ArmSwitchState {
  /** Bound to the switch — ALWAYS the authoritative backend state. */
  checked: boolean;
  /** Locked while a write is in flight or an external reason disables it. */
  disabled: boolean;
  /** In-progress copy, or `null` when no write is pending. */
  label: 'Apagando…' | 'Prendiendo…' | null;
  /** True once the WS state echoes the pending intent (write applied). */
  settled: boolean;
}

/**
 * Resolve the AUTHORITATIVE armed bool from a WS state snapshot, with the
 * back-compat fallback the whole opener page uses: the rule's REAL `ruleArmed`
 * when the envelope carries it, else (older backend during rollout) the
 * pair-level `pairEnabled`. Centralised here so the switch render, the
 * wait-for-echo predicate and the confirmed check all resolve "is the rule
 * armed?" the SAME way.
 */
export function resolveWsArmed(opts: {
  /** The rule's real armed state from the WS envelope; `undefined` when the
   *  backend hasn't shipped the field yet. */
  ruleArmed: boolean | undefined;
  /** Pair-level opener kill switch — the back-compat fallback. */
  pairEnabled: boolean;
}): boolean {
  return opts.ruleArmed ?? opts.pairEnabled;
}

export function resolveArmSwitchState(opts: {
  /** The rule's REAL armed state from the WS envelope (authoritative). When
   *  `undefined` (old backend during rollout) the switch falls back to
   *  `pairEnabled`. */
  ruleArmed: boolean | undefined;
  /** Pair-level opener kill switch from the WS envelope — the back-compat
   *  fallback for `checked` when `ruleArmed` is missing. */
  pairEnabled: boolean;
  /** The operator's last on/off intent; `undefined` = no write pending. */
  pending: boolean | undefined;
  /** External disable reason (e.g. mutation loading, no pair id). */
  disabled?: boolean;
}): ArmSwitchState {
  const { ruleArmed, pairEnabled, pending, disabled = false } = opts;
  // Authoritative armed state: the rule's REAL `ruleArmed` when present,
  // else the pair-level kill switch (rollout back-compat). Never the
  // optimistic `pending` — that's what makes "OFF while the rule is still
  // armed" unrepresentable.
  const wsArmed = resolveWsArmed({ ruleArmed, pairEnabled });
  const inFlight = pending !== undefined;
  return {
    checked: wsArmed,
    disabled: disabled || inFlight,
    label: !inFlight ? null : pending ? 'Prendiendo…' : 'Apagando…',
    settled: inFlight && wsArmed === pending,
  };
}
